/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/ru";

// antd:
// import { LoadingOutlined } from "@ant-design/icons";
import { Form, DatePicker, TimePicker } from "antd";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";
import customParseFormat from "dayjs/plugin/customParseFormat";

// import DivE from "../react/DivE.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";
// Reducers:
import { getLocale, getLng, getDict, getTheme, getAppState, changeAppState, getUser, getSafePtr } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

// const { Option } = Select;
const { rz } = window;
dayjs.extend(customParseFormat);

type Enumerate<N extends number, Acc extends number[] = []> = Acc["length"] extends N ? Acc[number] : Enumerate<N, [...Acc, Acc["length"]]>
type IntRange<F extends number, T extends number> = Exclude<Enumerate<T>, Enumerate<F>>

type Tdtpicker = {
  picker:"time" | "date" | "week" | "month" | "quarter" | "year",
  range?:boolean,
  allowClear?:boolean,
  variant:"outlined" | "borderless" | "filled",
  inputReadOnly?:boolean,
  mode?:"time" | "date" | "month" | "year" | "decade",
  placement?:"bottomLeft" | "bottomRight" | "topLeft" | "topRight",
  size?:"large" | "middle" | "small",
  format?:string,
  showNow?:boolean,
  showTime?:{format:string},
  showToday?:boolean,
  hourStep?:IntRange<1, 23>,
  minuteStep?:IntRange<1, 59>
  secondStep?:IntRange<1, 59>
};

const RZDatePicker:FC<TuniversalElementProps> = ({attributes, children}) => {
  RZDatePicker.displayName = "DatePicker";
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const locale = useAppSelector(getLocale);
  const user = useAppSelector(getUser);
  const dct = useAppSelector(getDict);
  const lng = useAppSelector(getLng);
  const theme = useAppSelector(getTheme);
  const safe = useAppSelector(getSafePtr);
  const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath, type} = attributes;
  const appState = useAppSelector(getAppState(rootAttributes.key)), elState = appState[fullPath];
  const {color, backgroundColor, required} = elState.attributes;
  const rootNamespace = useMemo(() => (window[rootAttributes.key || rz.strMakeID(24)]), [rootAttributes.key]);
  const isParentForm = useMemo(() => {
    const parentPath = rz.strParentFolderName(fullPath || ""), parentState = appState[parentPath];
    return Boolean(parentState.name === "form");
  }, [appState, fullPath]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const dtpType = useMemo<Tdtpicker>(() => {
    const typ:Tdtpicker = {picker: "date", size: "middle", allowClear: false, variant: "outlined"};
    (type || "").replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt) => {
      if (~["small", "middle", "large"].indexOf(tt)) { typ.size = tt }
      if (~["allowClear", "allowclear", "clearButton", "clearbutton"].indexOf(tt)) { typ.allowClear = true }
      else if (~["noborders", "borderless"].indexOf(tt)) { typ.variant = "borderless" }
      if (~["time", "week", "month", "quarter", "year"].indexOf(tt)) { typ.picker = tt }
      if (~["YYYY-MM-DD", "YYYY-MM", "YYYY", "DD.MM.YYYY", "YYYY-[Q]Q"].indexOf(tt)) { typ.format = tt }
      if (~["bottomLeft", "bottomRight", "topLeft", "topRight"].indexOf(tt)) { typ.placement = tt }
      if (~["inputReadOnly", "readOnly", "readonly"].indexOf(tt)) { typ.inputReadOnly = true }
      if (~["showTime", "showtime"].indexOf(tt)) { typ.showTime = { format: "HH:mm" } }
      if (~["showToday", "showtoday"].indexOf(tt)) { typ.showToday = true }
      if (~["showNow", "shownow"].indexOf(tt)) { typ.showNow = true }
      if (~["range", "daterange", "dateRange", "timerange", "timeRange"].indexOf(tt)) { typ.range = true }

      if (attributes?.size) { typ.size = attributes.size }
      if (attributes?.format) { typ.format = attributes.format }
      if (attributes?.variant) { typ.variant = attributes.variant }
      if (attributes?.allowclear === "true" || attributes?.allowClear === "true") { typ.allowClear = true }
      // @ts-ignore
      if (attributes?.hourStep || attributes?.hourstep) { typ.hourStep = Number(attributes?.hourStep || attributes?.hourstep) }
      // @ts-ignore
      if (attributes?.minuteStep || attributes?.minutestep) { typ.minuteStep = Number(attributes?.minuteStep || attributes?.minutestep) }
      // @ts-ignore
      if (attributes?.secondStep || attributes?.secondstep) { typ.secondStep = Number(attributes?.secondStep || attributes?.secondstep) }
      if (attributes?.range || attributes?.dateRange || attributes?.timeRange) { typ.range = (attributes?.range || attributes?.dateRange || attributes?.timeRange) === "true" }
      if (attributes?.placement && ~["bottomLeft", "bottomRight", "topLeft", "topRight"].indexOf(attributes.placement)) {
        typ.placement = attributes.placement;
      }
    });
    return typ;
  }, [type, attributes]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    // if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    return userClss;
  }, [elState?.userClass]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    let clr:string; const allc = (color || "").split("|"); if (allc.length > 1) { clr = (theme === "dark" ? allc[1] : allc[0]) } else { clr = allc[0] }
    if (!est?.color && clr) { est.color = clr }
    let bclr:string; const allb = (backgroundColor || "").split("|"); if (allb.length > 1) { bclr = (theme === "dark" ? allb[1] : allb[0]) } else { bclr = allb[0] }
    if (!est?.backgroundColor && bclr) { est.backgroundColor = bclr }
    return est;
  }, [elState?.userStyle, elState?.visible, color, backgroundColor, theme, permissions]);

  const placeholder = useMemo(() => {
    const placeholderStr = attributes["placeholder_" + lng] || attributes.placeholder || attributes["placeholder_" + rlng] || "";

    // для range - проверяем разделитель |
    if (dtpType.range) {
      if (placeholderStr.includes("|")) {
        const parts = placeholderStr.split("|");

        return [parts[0]?.trim() || "", parts[1]?.trim() || ""];
      }

      return [placeholderStr, placeholderStr];
    }

    return placeholderStr;
  }, [attributes, lng, rlng, dtpType.range]);

  const dtValue = useMemo(() => {
    let vv; // dayjs()
    if (elState?.value) {
      if (rz.isArray(elState.value) && elState.value[0]) {
        // @ts-ignore
        vv = (
          dtpType.format
            ? [dayjs(elState.value[0], dtpType.format), (elState.value[1] && dayjs(elState.value[1], dtpType.format)) || dayjs(elState.value[0], dtpType.format)]
            : [dayjs(elState.value[0]), (elState.value[1] && dayjs(elState.value[1])) || dayjs(elState.value[0])]
        );
      }
      else if (dtpType.range && (!elState.value || (rz.isArray(elState.value) && (!elState.value.length || (elState.value[0] === "" && elState.value[1] === ""))))) {
        vv = [dayjs(), dayjs()];
      }
      else {
      // обработка для quarter формата
        let formatToUse = dtpType.format;
        if (!formatToUse && dtpType.picker === "quarter") {
          formatToUse = "YYYY-[Q]Q";
        }
        vv = (formatToUse ? dayjs(elState.value, formatToUse) : dayjs(elState.value));
      }
    }
    return vv;
  }, [dtpType.format, dtpType.range, dtpType.picker, elState.value]);

  // eslint-disable-next-line react-hooks/exhaustive-deps, max-len
  const title = useMemo<string | undefined>(() => (elState.attributes["title_" + lng] || elState.attributes.title || elState.attributes["title_" + rlng] || attributes["title_" + lng] || attributes.title || attributes["title_" + rlng]), [elState.attributes, attributes, lng]);
  // eslint-disable-next-line max-len, react-hooks/exhaustive-deps
  const title2 = useMemo<string | undefined>(() => (elState?.attributes["title2_" + lng] || elState?.attributes.title2 || elState?.attributes["title2_" + rlng] || attributes["title2_" + lng] || attributes.title2 || attributes["title2_" + rlng]), [elState?.attributes, attributes, lng]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tooltip = useMemo<string | undefined>(() => (elState.attributes["tooltip_" + lng] || elState.attributes.tooltip || elState.attributes["tooltip_" + rlng]), [elState.attributes, lng]);

  const onChange = useCallback((date, dateString) => { // typeof DatePickerProps["onChange"]
    if (fnc.config?.isDev) console.log({action: "<DatePicker key='" + key + "'> -> onChange()", value: dateString});
    dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {...elState, value: dateString}}));
    if (rootNamespace && (attributes.onChange || attributes.onchange) && rootNamespace[attributes.onChange || attributes.onchange]) {
      const clientFNC = rootNamespace[attributes.onChange || attributes.onchange];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value: dateString, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
          if (ret && ret.navigate) { navigate(ret.navigate.to, {replace: Boolean(ret.navigate.replace)}); console.log("navigate(" + ret.navigate.to + ")") }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value: dateString, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
        if (ret && ret.navigate) { navigate(ret.navigate.to, {replace: Boolean(ret.navigate.replace)}); console.log("navigate(" + ret.navigate.to + ")") }
      }
    }
  }, [key, dispatch, rootAttributes.key, fullPath, elState, rootNamespace, attributes, user, lng, dct, navigate, safe]);

  const disabledDate = useCallback((vCurrent, vType) => {
    // if (fnc.config?.isDev) console.log({action: "<DatePicker key='" + key + "'> -> disabledDate()", vCurrent, vType});
    const attr = (attributes.disabledDate || attributes.disableddate);
    if (attr && rootNamespace && rootNamespace[attr]) {
      const clientFNC = rootNamespace[attr];
      if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, vCurrent, vType, safe});
        return ret;
      }
      return undefined;
    }
    return undefined;
  }, [key, rootAttributes.key, rootNamespace, attributes, user, lng, dct, safe]);

  const disabledTime = useCallback((vCurrent, vType) => {
    // if (fnc.config?.isDev) console.log({action: "<DatePicker key='" + key + "'> -> disabledTime()", vCurrent, vType});
    const attr = (attributes.disabledTime || attributes.disabledtime);
    if (attr && rootNamespace && rootNamespace[attr]) {
      const clientFNC = rootNamespace[attr];
      if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, vCurrent, vType, safe});
        return ret;
      }
      return undefined;
    }
    return undefined;
  }, [key, rootAttributes.key, rootNamespace, attributes, user, lng, dct, safe]);

  const wrapperForm = useCallback((mainComponent:React.ReactNode) => {
    if (isParentForm) {
      const frmItemProps:{[key:string]:any} = {
        key: key + "-formitem", label: (<span className="rz-txt-std">{title2}</span>), className: "rz-width-expand",
        style: ((elState?.visible === false || permissions.visible === false) ? {display: "none"} : undefined)
      };
      // eslint-disable-next-line max-len
      if (required) { frmItemProps.name = String(key); frmItemProps.rules = (required ? [{required: true, message: (attributes["requiredText_" + lng] || attributes.requiredText || attributes["requiredText_" + rlng] || "") || "Required field!"}] : []); frmItemProps.getValueProps = () => (elState.value) }
      return (
        <Form.Item {...frmItemProps}>
          {mainComponent}
        </Form.Item>
      )
    }
    return mainComponent;
  }, [isParentForm, title2, key, required, attributes, lng, rlng, elState?.visible, elState.value, permissions.visible]);

  if (dtpType.picker === "time") {
    if (dtpType.range) {
      return wrapperForm(
        <TimePicker.RangePicker
          key={key} style={styl} className={clsu.join(" ")} uk-tooltip={tooltip}
          locale={locale}
          // @ts-ignore
          defaultValue={dtValue ? (rz.isArray(dtValue) ? dtValue : [dtValue, dtValue]) : undefined}
          format={dtpType.format}
          placeholder={Array.isArray(placeholder) ? placeholder as [string, string] : [placeholder, placeholder]}
          allowClear={dtpType.allowClear}
          variant={dtpType.variant}
          size={dtpType.size}
          placement={dtpType.placement}
          showNow={dtpType.showNow}
          inputReadOnly={dtpType.inputReadOnly}
          hourStep={dtpType.hourStep}
          minuteStep={dtpType.minuteStep}
          secondStep={dtpType.secondStep}
          onChange={onChange}
          disabled={elState?.disabled || permissions.disabled === true}
          disabledTime={disabledTime}
        />
      );
    }

    return wrapperForm(
      <TimePicker
        key={key} style={styl} className={clsu.join(" ")} uk-tooltip={tooltip}
        locale={locale}
        defaultValue={dtValue}
        format={dtpType.format}
        placeholder={typeof placeholder === "string" ? placeholder : placeholder[0]}
        allowClear={dtpType.allowClear}
        variant={dtpType.variant}
        size={dtpType.size}
        placement={dtpType.placement}
        showNow={dtpType.showNow}
        inputReadOnly={dtpType.inputReadOnly}
        hourStep={dtpType.hourStep}
        minuteStep={dtpType.minuteStep}
        secondStep={dtpType.secondStep}
        onChange={onChange}
        disabled={elState?.disabled || permissions.disabled === true}
        // @ts-ignore
        disabledTime={disabledTime}
      />
    );
  }

  if (dtpType.range) {
    return wrapperForm(
      <DatePicker.RangePicker
        key={key} style={styl} className={clsu.join(" ")} uk-tooltip={tooltip}
        locale={locale}
        // @ts-ignore
        // eslint-disable-next-line max-len
        defaultValue={(elState.value && ((rz.isDate(elState.value) || (rz.isString(elState.value) && elState.value !== "")) || (elState.value.length && (rz.isDate(elState.value[0]) || elState.value[0] !== "")))) ? (rz.isArray(dtValue) ? dtValue : [dtValue, dtValue]) : undefined}
        // eslint-disable-next-line max-len
        value={(elState.value && ((rz.isDate(elState.value) || (rz.isString(elState.value) && elState.value !== "")) || (elState.value.length && (rz.isDate(elState.value[0]) || elState.value[0] !== "")))) ? (rz.isArray(dtValue) ? dtValue : [dtValue, dtValue]) : undefined}
        format={dtpType.format}
        placeholder={Array.isArray(placeholder) ? placeholder as [string, string] : [placeholder, placeholder]}
        picker={dtpType.picker}
        allowClear={dtpType.allowClear}
        variant={dtpType.variant}
        size={dtpType.size}
        placement={dtpType.placement}
        showNow={dtpType.showNow}
        showTime={dtpType.showTime}
        inputReadOnly={dtpType.inputReadOnly}
        hourStep={dtpType.hourStep}
        minuteStep={dtpType.minuteStep}
        secondStep={dtpType.secondStep}
        onChange={onChange}
        disabled={elState?.disabled || permissions.disabled === true}
        disabledTime={disabledTime}
        disabledDate={disabledDate}
      />
    );
  }

  return wrapperForm(
    <DatePicker
      key={key} style={styl} className={clsu.join(" ")} uk-tooltip={tooltip}
      locale={locale}
      defaultValue={dtValue}
      value={dtValue ?? undefined}
      format={dtpType.format}
      placeholder={typeof placeholder === "string" ? placeholder : placeholder[0]}
      picker={dtpType.picker}
      allowClear={dtpType.allowClear}
      variant={dtpType.variant}
      size={dtpType.size}
      placement={dtpType.placement}
      showToday={dtpType.showToday}
      showNow={dtpType.showNow}
      showTime={dtpType.showTime}
      inputReadOnly={dtpType.inputReadOnly}
      hourStep={dtpType.hourStep}
      minuteStep={dtpType.minuteStep}
      secondStep={dtpType.secondStep}
      onChange={onChange}
      disabled={elState?.disabled || permissions.disabled === true}
      disabledDate={disabledDate}
      // @ts-ignore
      disabledTime={disabledTime}
    />
  );
}

export default RZDatePicker;
/* eslint-enable @typescript-eslint/no-unused-vars */
